import railsFetch from "../railsFetch";
import { BulkOperation, BulkOperationPayload } from "./types";

const TERMINAL_STATUSES: BulkOperation["status"][] = ["complete", "failed"];

// One poll per operation, shared across callers (mirrors pollExportStatus).
const activeOperationPolls = new Map<number, () => void>();

// Polls an operation until it settles, then calls onSettled exactly once so
// the caller can refresh whatever the operation just changed (GR-2202: the
// grid kept showing pre-operation statuses until a manual page refresh).
// Returns a cancel function for unmount cleanup.
export const pollBulkOperation = (
  bulkOperationsUrl: string,
  operationId: number,
  onSettled: (operation: BulkOperation) => void
): (() => void) => {
  const existingCancel = activeOperationPolls.get(operationId);
  if (existingCancel) {
    return existingCancel;
  }

  // Bulk operations batch 100 records per job hop, so large selections run
  // for minutes. 200 * 3s = 10 minutes; past that we stop quietly and the
  // user falls back to today's behaviour (refresh by hand).
  const maxAttempts = 200;
  let attempts = 0;
  let cancelled = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const poll = async (): Promise<void> => {
    if (cancelled) return;

    if (attempts >= maxAttempts) {
      console.warn("[pollBulkOperation] Polling timeout reached");
      activeOperationPolls.delete(operationId);
      return;
    }

    attempts += 1;
    try {
      const response = await railsFetch(
        `${bulkOperationsUrl}/${operationId}.json`,
        { method: "GET", headers: { Accept: "application/json" } }
      );
      if (response.ok) {
        const operation: BulkOperation = await response.json();
        if (TERMINAL_STATUSES.includes(operation.status)) {
          activeOperationPolls.delete(operationId);
          if (!cancelled) onSettled(operation);
          return;
        }
      }
    } catch (error) {
      // Transient fetch failures just consume an attempt and retry.
      console.error("[pollBulkOperation] Error:", error);
    }

    if (!cancelled) {
      timeoutId = setTimeout(poll, 3000);
    }
  };

  const cancel = () => {
    cancelled = true;
    if (timeoutId) clearTimeout(timeoutId);
    activeOperationPolls.delete(operationId);
  };

  activeOperationPolls.set(operationId, cancel);
  timeoutId = setTimeout(poll, 3000);
  return cancel;
};

export const startBulkOperation = async (
  bulkOperationsUrl: string,
  payload: BulkOperationPayload
): Promise<BulkOperation | null> => {
  try {
    const response = await railsFetch(bulkOperationsUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      const data: BulkOperation = await response.json();

      window.dispatchEvent(
        new CustomEvent("bulkOperationStarted", {
          detail: { operationId: data.id, actionKey: data.actionKey },
        })
      );

      return data;
    } else {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.errors?.join(", ") || "Failed to start bulk operation";
      console.error("[startBulkOperation] Failed:", errorMessage);
      return null;
    }
  } catch (error) {
    console.error("[startBulkOperation] Error:", error);
    return null;
  }
};
